/**
 * Getting and keeping a connection to the relay.
 *
 * Two things the SDK does not do for us. Its tokens last an hour (measured against
 * api.adamohq.com), so one has to be fetched again before a long session runs out; and the
 * zenoh client underneath does not reconnect after its WebSocket drops -- it only retries the
 * very first connect -- so a dropped socket has to be noticed here and the provider rebuilt
 * around a fresh one.
 *
 * The refresh and the reconnect are deliberately separate. A fresh token is kept in hand at
 * all times but is *not* pushed into a live session, because changing it reopens the socket
 * and that costs the operator a second or two of video in the middle of a grasp. It is used
 * at the next connect, whenever that turns out to be.
 */
import { AdamoProvider, resolveNearestRouter } from "adamo-react";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { fetchRelaySession, type Me, type RelaySession } from "../api/client";

const REFRESH_BEFORE_S = 300; // fetch the next token five minutes before this one dies
const RETRY_MIN_MS = 1000;
const RETRY_MAX_MS = 30000;

type Relay = RelaySession & { url: string };

type Connection = {
  me: Me | null;
  org: string | null;
  robot: string;
  operator: string;
  trouble: string | null;
  connecting: boolean;
  /** Tell the supervisor the session looks dead; it decides whether to rebuild it. */
  reportTrouble: (why: string) => void;
};

const ConnectionContext = createContext<Connection | null>(null);

export const useConnection = (): Connection => {
  const value = useContext(ConnectionContext);
  if (!value) throw new Error("useConnection outside the provider");
  return value;
};

/** `me` is who the backend already said this is: nobody gets this far without being on its list. */
export function ConnectionProvider({ me, children }: { me: Me; children: React.ReactNode }) {
  const [relay, setRelay] = useState<Relay | null>(null);
  const [epoch, setEpoch] = useState(0);
  const [trouble, setTrouble] = useState<string | null>(null);
  const freshest = useRef<Relay | null>(null);
  const retryAt = useRef(RETRY_MIN_MS);
  const rebuilding = useRef(false);

  const obtain = useCallback(async (): Promise<Relay> => {
    const session = await fetchRelaySession();
    // Relay selection probes candidates from wherever the browser is, so it has to happen
    // here rather than in the backend, which may be in another part of the world entirely.
    const url = await resolveNearestRouter({
      apiUrl: "https://api.adamohq.com",
      token: session.token,
      org: session.org,
      orgId: session.orgId,
      fallbackUrl: session.wssUrl,
    });
    const next: Relay = { ...session, url: url || session.wssUrl };
    freshest.current = next;
    return next;
  }, []);

  // Connect, and reconnect whenever the epoch moves.
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const connect = async () => {
      try {
        const next = await obtain();
        if (cancelled) return;
        setRelay(next);
        setTrouble(null);
        retryAt.current = RETRY_MIN_MS;
      } catch (err) {
        if (cancelled) return;
        setTrouble(`cannot reach the relay: ${err instanceof Error ? err.message : String(err)}`);
        timer = window.setTimeout(connect, retryAt.current);
        retryAt.current = Math.min(retryAt.current * 2, RETRY_MAX_MS);
      } finally {
        rebuilding.current = false;
      }
    };
    void connect();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [epoch, obtain]);

  // Keep a fresh token in hand without disturbing the live session.
  useEffect(() => {
    if (!relay) return;
    const due = Math.max(5000, (relay.expiresAt - REFRESH_BEFORE_S) * 1000 - Date.now());
    const timer = window.setTimeout(() => { void obtain().catch(() => undefined); }, due);
    return () => window.clearTimeout(timer);
  }, [relay, obtain]);

  const reportTrouble = useCallback((why: string) => {
    if (rebuilding.current) return;
    rebuilding.current = true;
    setTrouble(why);
    // Reconnecting means remounting the provider, which builds a new session from whatever
    // token is freshest. The old one is left to be collected; the SDK gives us no close.
    if (freshest.current) setRelay(freshest.current);
    setEpoch((n) => n + 1);
  }, []);

  const value: Connection = {
    me,
    org: relay?.org ?? null,
    robot: me.robot,
    operator: me.operator,
    trouble,
    connecting: relay === null,
    reportTrouble,
  };

  if (!relay) {
    return (
      <ConnectionContext.Provider value={value}>
        {children}
      </ConnectionContext.Provider>
    );
  }
  return (
    <ConnectionContext.Provider value={value}>
      <AdamoProvider
        key={epoch}
        url={relay.url}
        org={relay.org}
        orgId={relay.orgId}
        authToken={relay.token}
        onSessionOpenFailed={(err: unknown) => reportTrouble(`the relay refused the connection: ${String(err)}`)}
      >
        {children}
      </AdamoProvider>
    </ConnectionContext.Provider>
  );
}
